/**
 * Some of the cases are mixed between snake and kebab case, but this looks like it is due to
 * some kind of automatic appending of 'statusType', which uses kebab case (best guess).
 */

import {UnreliableBookingStatusIds} from "shared-types/bookingStatusTypes";

/**
 * In UAT (and I assume prod) the status id values are sometimes GUID-like values, rather than the static values in the
 * defaultStatus.json. We have many conditions that use the BookingStatusIds enum values throughout the app,
 * but these are no longer reliable, so we must use a different property. We can’t use Code because TX is used twice, so it has to be name.
 */

export enum BookingStatusNames {
  PendingPayment = 'Pending Payment',
  Unconfirmed = 'Unconfirmed',
  Confirmed = 'Confirmed',
  ConfirmedLeftMessage = 'Left Message',
  Cancelled = 'Cancelled',
  CancelledNoShow = 'No Show',
  SentSms = 'Sent SMS',
  SmsFailed = 'SMS Failed',
  PartiallySeated = 'Partially Seated',
  Seated = 'Seated',
  SeatedOrdered = 'Ordered',
  SeatedMain = 'Main',
  SeatedDessert = 'Dessert',
  SeatedBilled = 'Billed',
  SeatedPaid = 'Paid',
  Finished = 'Finished',
  StandbyPending = 'Standby Pending',
  FinishAll = 'Finish All'
}

export enum BookingStatusTypes {
  PendingPayment = 'pending-payment',
  Unconfirmed = 'unconfirmed',
  Confirmed = 'confirmed',
  PartiallySeated = 'partially-seated',
  Cancelled = 'cancelled',
  Seated = 'seated',
  Finished = 'finished',
  StandbyPending = 'standby-pending'
}

/**
 * These status objects should be the same as 'src/json/defaultStatus.json'.
 * The json gets added to couch when a new venue is created (via wizard) and also, in
 * 'src/app/services/venue.service.js' in 'setUp()', some statuses are added if they don't
 * already exist. This is the case for new statuses that were added for various features,
 * so that existing venues get their couchDB updated.
 */

export interface IBookingStatus {
  _id: UnreliableBookingStatusIds | string; // "status_unconfirmed";
  code: string; //"U",
  name: BookingStatusNames; //"Unconfirmed",
  statusType: BookingStatusTypes;
  enabled: boolean; //true,
  editable: boolean; //false
  hide?: boolean;
  statusEditable?: boolean;
  order?: number; // 0
  type?: string; //"status"
  _rev?: string;
}


export const StatusSentSms: IBookingStatus = {
  "_id": UnreliableBookingStatusIds.StatusSentSms,
  "code": "TX",
  "name": BookingStatusNames.SentSms,
  "statusType": BookingStatusTypes.PartiallySeated,
  "enabled": true,
  "editable": false,
  "hide": true
}

export const StatusSmsFailed: IBookingStatus = {
  "_id": UnreliableBookingStatusIds.StatusSmsFailed,
  "code": "TX",
  "name": BookingStatusNames.SmsFailed,
  "statusType": BookingStatusTypes.Unconfirmed,
  "enabled": true,
  "editable": false,
  "hide": true
}

export const StatusStandbyPending: IBookingStatus = {
  "_id": UnreliableBookingStatusIds.StatusStandbyPending,
  "code": "SP",
  "name": BookingStatusNames.StandbyPending,
  "statusType": BookingStatusTypes.StandbyPending,
  "enabled": true,
  "editable": false
}

export const StatusPendingPayment: IBookingStatus = {
  "_id": UnreliableBookingStatusIds.StatusPendingPayment,
  "code": "P$",
  "name": BookingStatusNames.PendingPayment,
  "statusType": BookingStatusTypes.PendingPayment,
  "enabled": true,
  "editable": false,
  "hide": true
}

export const StatusUnconfirmed: IBookingStatus = {
  "_id": UnreliableBookingStatusIds.StatusUnconfirmed,
  "code": "U",
  "name": BookingStatusNames.Unconfirmed,
  "statusType": BookingStatusTypes.Unconfirmed,
  "enabled": true,
  "editable": false
}

export const StatusConfirmed: IBookingStatus = {
  "_id": UnreliableBookingStatusIds.StatusConfirmed,
  "code": "C",
  "name": BookingStatusNames.Confirmed,
  "statusType": BookingStatusTypes.Confirmed,
  "enabled": true,
  "editable": false
}


export const StatusSeated: IBookingStatus = {
  "_id": UnreliableBookingStatusIds.StatusSeated,
  "code": "S",
  "name": BookingStatusNames.Seated,
  "statusType": BookingStatusTypes.Seated,
  "enabled": true,
  "editable": false
}

export const StatusNoShow: IBookingStatus = {
  "_id": UnreliableBookingStatusIds.StatusCancelledNoShow,
  "code": "NS",
  "name": BookingStatusNames.CancelledNoShow,
  "statusType": BookingStatusTypes.Cancelled,
  "enabled": true,
  "editable": true
}

export const StatusCancelled: IBookingStatus = {
  "_id": UnreliableBookingStatusIds.StatusCancelled,
  "code": "X",
  "name": BookingStatusNames.Cancelled,
  "statusType": BookingStatusTypes.Cancelled,
  "enabled": true,
  "editable": false
}

export const StatusFinished: IBookingStatus = {
  "_id": UnreliableBookingStatusIds.StatusFinished,
  "code": "F",
  "name": BookingStatusNames.Finished,
  "statusType": BookingStatusTypes.Finished,
  "enabled": true,
  "editable": false
}
